import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Euro, 
  TrendingUp, 
  Calendar, 
  Building2,
  User,
  ChevronLeft,
  ChevronRight,
  Download
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, startOfMonth, endOfMonth, isSameMonth, addMonths, subMonths } from "date-fns";
import { fr } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyCommissionsPage = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [stats, setStats] = useState({
    total: 0,
    currentMonth: 0,
    forecasts: {}
  });

  useEffect(() => {
    fetchMyCommissions();
  }, []);

  const calculateStats = useCallback(() => {
    const total = commissions.reduce((sum, c) => sum + (c.amount || 0), 0);
    
    // Commissions du mois sélectionné (incluant décommissions)
    const currentMonth = commissions
      .filter(c => {
        const date = parseISO(c.payment_date);
        return isSameMonth(date, selectedMonth);
      })
      .reduce((sum, c) => sum + (c.amount || 0), 0);
    
    // Prévisionnel par année (3 années suivantes) - incluant décommissions
    const currentYear = new Date().getFullYear();
    const forecasts = {};
    
    for (let i = 1; i <= 3; i++) {
      const year = currentYear + i;
      const yearTotal = commissions
        .filter(c => {
          try {
            const date = parseISO(c.payment_date);
            return date.getFullYear() === year;
          } catch {
            return false;
          }
        })
        .reduce((sum, c) => sum + (c.amount || 0), 0);
      
      forecasts[year] = yearTotal;
    }

    setStats({ total, currentMonth, forecasts });
  }, [commissions, selectedMonth]);

  useEffect(() => {
    calculateStats();
  }, [calculateStats]);

  const fetchMyCommissions = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer les commissions de contrats (anciennes)
      const contractCommResponse = await axios.get(`${API}/my-commissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Récupérer les commissions client (nouvelles)
      const clientCommResponse = await axios.get(`${API}/commissions/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Combiner les deux types de commissions
      const contractComms = contractCommResponse.data.map(c => ({...c, type: 'contract'}));
      const clientComms = clientCommResponse.data.commissions.map(c => ({
        ...c,
        type: 'client',
        payment_date: c.created_at // Utiliser created_at comme date de référence
      }));
      
      setCommissions([...contractComms, ...clientComms]);
    } catch (error) {
      console.error("Error fetching commissions:", error);
      if (error.response?.status === 403) {
        toast.error("Accès refusé : Fonctionnalité réservée aux commerciaux");
      } else {
        toast.error("Erreur lors du chargement des commissions");
      }
    } finally {
      setLoading(false);
    }
  };

  const getCommissionsByMonth = () => {
    // Filtrer par mois sélectionné
    const filtered = commissions.filter(c => {
      try {
        const date = parseISO(c.payment_date);
        return isSameMonth(date, selectedMonth);
      } catch {
        return false;
      }
    });

    // Trier par date (plus récent en premier)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.payment_date);
      const dateB = new Date(b.payment_date);
      return dateB - dateA;
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'dd MMMM yyyy', { locale: fr });
    } catch {
      return 'Date invalide';
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'paid') {
      return <Badge className="bg-emerald-100 text-emerald-800">Payée</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-800">À recevoir</Badge>;
  };

  const goToPreviousMonth = () => {
    setSelectedMonth(subMonths(selectedMonth, 1));
  };

  const goToNextMonth = () => {
    setSelectedMonth(addMonths(selectedMonth, 1));
  };

  const goToCurrentMonth = () => {
    setSelectedMonth(new Date());
  };

  const commissionsThisMonth = getCommissionsByMonth();

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Mes Commissions</h1>
        <p className="text-lg text-gray-600">Suivi en temps réel de vos commissions</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
        {/* Total Commissions avec Prévisionnel */}
        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardHeader className="pb-4">
            <CardDescription className="text-base font-medium text-gray-700">Total Commissions</CardDescription>
            <CardTitle className="text-4xl font-bold text-emerald-600">{formatCurrency(stats.total)}</CardTitle>
            <div className="flex items-center text-sm text-gray-600 mt-2">
              <Euro className="w-4 h-4 mr-1" />
              {commissions.length} commission(s)
            </div>
          </CardHeader>
          <CardContent className="pt-4 border-t border-emerald-100">
            <div className="text-sm font-semibold text-gray-700 mb-3">Prévisionnel 3 prochaines années</div>
            <div className="space-y-2">
              {Object.entries(stats.forecasts || {}).map(([year, amount]) => (
                <div key={year} className="flex items-center justify-between p-2 bg-white rounded border border-emerald-100">
                  <span className="font-medium text-gray-700">{year}</span>
                  <span className="text-emerald-600 font-semibold">{formatCurrency(amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Mois Sélectionné */}
        <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardHeader className="pb-4">
            <CardDescription className="text-base font-medium text-gray-700">Mois Sélectionné</CardDescription>
            <CardTitle className="text-4xl font-bold text-blue-600">{formatCurrency(stats.currentMonth)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center text-base text-gray-700 font-medium">
              <Calendar className="w-5 h-5 mr-2" />
              {format(selectedMonth, 'MMMM yyyy', { locale: fr })}
            </div>
            <div className="mt-4 text-sm text-gray-600">
              {commissionsThisMonth.length} commission(s) ce mois
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Month Navigation */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">
              Commissions - {format(selectedMonth, 'MMMM yyyy', { locale: fr })}
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousMonth}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToCurrentMonth}
              >
                Aujourd&apos;hui
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextMonth}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <CardDescription>
            {commissionsThisMonth.length} commission(s) ce mois
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Commissions List */}
      {commissionsThisMonth.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Euro className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">Aucune commission pour ce mois</p>
            <p className="text-sm text-gray-400">
              Sélectionnez un autre mois ou attendez de nouvelles ventes
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {commissionsThisMonth.map((commission) => (
            <Card key={commission.id} className={`hover-lift border-l-4 shadow-md ${
              commission.type === 'client' ? 'border-purple-500' : 'border-emerald-500'
            }`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-4">
                    {/* Badge Type de Commission */}
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={commission.type === 'client' ? 'bg-purple-100 text-purple-800' : 'bg-emerald-100 text-emerald-800'}>
                        {commission.type === 'client' ? '👤 Commission Client' : '📄 Commission Contrat'}
                      </Badge>
                      {commission.status === 'pending' && (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                          ⏳ En Attente
                        </Badge>
                      )}
                      {commission.status === 'validated' && (
                        <Badge className="bg-green-100 text-green-800">
                          ✅ Validée
                        </Badge>
                      )}
                      {commission.status === 'paid' && (
                        <Badge className="bg-blue-100 text-blue-800">
                          💰 Payée
                        </Badge>
                      )}
                      {commission.item_type === 'decommission' && (
                        <Badge className="bg-red-100 text-red-800">DÉCOMMISSION</Badge>
                      )}
                    </div>

                    {/* Affichage selon le type */}
                    {commission.type === 'client' ? (
                      // COMMISSION CLIENT
                      <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-100">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-1">
                              {commission.client_name || 'Client inconnu'}
                            </h3>
                            <p className="text-sm text-gray-600">
                              Rôle: <span className="font-semibold">{commission.user_role === 'teleprospecteur' ? 'Téléprospecteur' : 'Commercial'}</span>
                            </p>
                          </div>
                        </div>
                        {commission.statut_pipeline && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600 mt-2 pt-2 border-t border-purple-100">
                            <span>Statut Pipeline: <span className="font-semibold">{commission.statut_pipeline}</span></span>
                          </div>
                        )}
                        {commission.validated_at && (
                          <div className="text-xs text-green-600 mt-2">
                            ✅ Validée le {formatDate(commission.validated_at)}
                          </div>
                        )}
                      </div>
                    ) : (
                      // COMMISSION CONTRAT (Ancien système)
                      <div className="bg-gradient-to-r from-emerald-50 to-blue-50 p-4 rounded-lg border border-emerald-100">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-1">
                              {commission.company_name || commission.client_name || 'Client inconnu'}
                            </h3>
                            {commission.company_name && commission.client_name && (
                              <p className="text-sm text-gray-600 mb-2">
                                Contact: {commission.client_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 mb-2">
                          <Building2 className="w-5 h-5 text-emerald-600" />
                          <span className="text-base">
                            Fournisseur: <span className="font-bold text-emerald-700">{commission.supplier_name || 'N/A'}</span>
                          </span>
                        </div>
                        {/* Dates du contrat */}
                        {commission.contract_start_date && commission.contract_end_date && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600 mt-2 pt-2 border-t border-emerald-100">
                            <Calendar className="w-4 h-4" />
                            <span>
                              Contrat: {formatDate(commission.contract_start_date)} → {formatDate(commission.contract_end_date)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Date de paiement */}
                    <div className="flex items-center space-x-2 text-base">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">
                        {commission.type === 'client' ? 'Créée le' : 'Échéance'}: 
                        <span className="font-bold text-blue-700 ml-1">{formatDate(commission.payment_date)}</span>
                      </span>
                    </div>

                    {/* Détails Commission - uniquement pour contrats */}
                    {commission.type !== 'client' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-gray-50 p-3 rounded">
                        <div>
                          <div className="text-gray-500 text-xs mb-1">Type</div>
                          <div className="font-semibold capitalize">{commission.type || 'N/A'}</div>
                        </div>
                        
                        <div>
                          <div className="text-gray-500 text-xs mb-1">Fréquence</div>
                          <div className="font-semibold capitalize">{commission.payment_frequency || 'N/A'}</div>
                        </div>
                        
                        {commission.total_installments > 1 && (
                          <div>
                            <div className="text-gray-500 text-xs mb-1">Échéance</div>
                            <div className="font-semibold">
                              {commission.installment_number}/{commission.total_installments}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {commission.notes && (
                      <div className="text-sm text-gray-700 bg-blue-50 p-3 rounded border-l-4 border-blue-400">
                        <span className="font-semibold">Note:</span> {commission.notes}
                      </div>
                    )}
                  </div>

                  {/* Montant et Status - Plus visible */}
                  <div className="text-right space-y-3 ml-6 min-w-[200px]">
                    <div className={`text-5xl font-bold ${commission.item_type === 'decommission' ? 'text-red-600' : 'text-emerald-600'}`}>
                      {formatCurrency(commission.amount)}
                    </div>
                    {commission.item_type === 'decommission' ? (
                      <Badge className="bg-red-100 text-red-800">DÉCOMMISSION</Badge>
                    ) : (
                      getStatusBadge(commission.status)
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Résumé du mois */}
      {commissionsThisMonth.length > 0 && (
        <Card className="bg-gradient-to-r from-emerald-500 to-blue-500 border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between text-white">
              <div>
                <h3 className="text-2xl font-bold">
                  Total {format(selectedMonth, 'MMMM yyyy', { locale: fr })}
                </h3>
                <p className="text-emerald-50 mt-1">
                  {commissionsThisMonth.length} commission(s) ce mois
                </p>
              </div>
              <div className="text-right">
                <div className="text-5xl font-bold">
                  {formatCurrency(stats.currentMonth)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MyCommissionsPage;
